<template>
  <div class="page" :style="finalStyle">
    <NavigationBar :title="$t('message.orderTitle')" />
    <ContentWrapper class="list-wrapper" :empty="empty" :loading="pageLoading" :error="pageError"
      :empty-props="{ icon: 'order', title: $t('message.orderEmptyTitle'), desc: $t('message.orderEmptyDesc') }"
      @reload="refreshData">
      <!-- 消息列表 -->
      <List class="message-list" v-model:loading="pageLoading" v-model:error="loadMoreError"
        :finished="pageError || !hasMore"
        :finished-text="pageLoaded && messageGroups.length > 0 ? $t('message.noMore') : ''"
        :error-text="$t('message.loadError')" @load="refreshData">
        <template #loading>
          <Loading size="24px" />
        </template>
        <div class="container">
          <div v-for="group in messageGroups" class="message-group">
            <h3 class="time">{{ group.time }}</h3>
            <div v-for="item in group.items" class="message-item" @click="goTargetPage(item)">
              <div class="message-header">
                <img :src="item.icon" class="icon">
                <h3 class="title">{{ item.title }}</h3>
              </div>
              <div class="message-body">
                <img :src="item.mainImage" class="cover" />
                <div class="right">
                  <p class="m-content">{{ item.content }}</p>
                  <div class="right-bottom" v-if="item.subContent">
                    <div class="m-type-label">{{ $t('message.' + getTypeLabel(item.messageType)) }}:</div>
                    <div class="m-sub-content">{{ item.subContent }}</div>
                  </div>
                </div>
              </div>
              <div class="message-footer">
                <Button class="btn-view-more" plain round>{{ $t('message.viewMore') }}</Button>
              </div>
            </div>
          </div>
        </div>
      </List>
    </ContentWrapper>
    <!-- 分类商品 tabs -->
    <GoodsTabs v-if="pageReady && empty" />
  </div>
</template>

<script setup lang="ts">
import { ref, provide } from 'vue'
import { getDeliveryMessageList } from '@/api/index'
import { navigateTo, logAFEvent } from '@voghion/native-api'
import { computed } from 'vue'
import GoodsTabs from '@/components/GoodsTabs.vue'
import ContentWrapper from '@/components/ContentWrapper.vue'
import { Loading, Button, List } from 'vant'
import NavigationBar from '../components/NavigationBar.vue'
import { useEnvStore } from '@/states/env'
import { storeToRefs } from 'pinia'
import { OrderMessageType } from '@/utils/constant'

const PAGE_NAME = 'orderMessagePage'
provide('pageName', PAGE_NAME)
provide('goodsSpmName', 'orderMessageRecomGoods')
provide('goodsSpm', '01010440201')

const store = useEnvStore()
const { pagePaddingTop } = storeToRefs(store)
const finalStyle = computed(() => {
  return {
    paddingTop: `${pagePaddingTop.value}px`
  }
})

const pageReady = ref(false)
const pageError = ref(null)
const pageLoading = ref(false)
const pageLoaded = ref(false)
/**
 * 消息列表
 */
const list = ref([])
const hasMore = ref(true)
const pageNow = ref(1)
const pageSize = ref(10)
const empty = computed(() => {
  return pageReady.value && !pageLoading.value && !list.value.length
})
const getTypeLabel = (type) => {
  switch (type) {
    case OrderMessageType.Refunded:
      return 'reason'
    default:
      return 'latestProgress'
  }
}

/**
 * 消息分组，将相同时间的消息合并
 */
const messageGroups = computed(() => {
  const groups = []
  let group = null
  for (const item of list.value) {
    if (group && group.time !== item.messageTime) {
      groups.push(group)
      group = null
    }
    if (!group) {
      group = {
        time: item.messageTime,
        items: []
      }
    }
    group.items.push(item)
  }
  if (group) {
    groups.push(group)
  }
  return groups
})


/**
 * 查询消息
 */
const fetchMessageData = async () => {
  let data = null, error = null
  try {
    const res = await getDeliveryMessageList({
      pageNow: pageNow.value,
      pageSize: pageSize.value
    })
    if (res && res.rtn_code === '0000') {
      data = res.data
      const items = data.tradeDeliveryInfos || []
      list.value = pageNow.value > 1 ? list.value.concat(items) : items
      hasMore.value = items.length > 0 && data.totalCount > pageNow.value * pageSize.value
      if (hasMore.value) {
        pageNow.value++
      }
      pageLoaded.value = true
    } else {
      error = new Error('server error')
    }
  } catch (err) {
    error = err
  }
  return {
    data, error
  }
}
const loadMoreError = ref(false)
const pageViewed = ref(false)
const refreshData = async () => {
  pageError.value = null
  pageLoading.value = true
  const res = await fetchMessageData()
  let hasError = false
  if (res.error) {
    hasError = true
    if (pageNow.value <= 1) {
      pageError.value = res.error
    } else {
      loadMoreError.value = true
    }
  }
  if (!hasError) {
    pageReady.value = true
    if (!pageViewed.value) {
      pageViewed.value = true
      logAFEvent({
        event: 'showSPM',
        spm: '01010440000',
        spmName: PAGE_NAME,
        params: {
          if_have_message: messageGroups.value.length > 0 ? 1 : 0
        }
      })
    }
  }
  pageLoading.value = false
}

/**
 * 跳转目标页面
 * @param item
 */
const goTargetPage = (item: { routeUrl: string }) => {
  item.routeUrl && navigateTo({
    url: item.routeUrl
  })
}
</script>

<style lang="scss" scoped>
.page {
  width: min(500px, 100%);
  margin: 0 auto;
  min-height: 100vh;
  box-sizing: border-box;
  background: #F8F8F8;

  .list-wrapper {
    padding: 0 0 16px;

    .message-list {
      .message-group {
        padding: 0 12px;

        +.message-group {
          margin-top: 14px;
        }

        .time {
          margin: 0;
          padding: 10px 0;
          font-weight: 400;
          font-size: 12px;
          color: #666666;
          line-height: 17px;
          text-align: center;
        }

        .message-item {
          background: #FFFFFF;
          border-radius: 4px;
          padding: 8px 8px 10px;

          &:active {
            background: $c-bg-gray3;
          }

          +.message-item {
            margin-top: 10px;
          }

          .message-header {
            display: flex;
            align-items: center;

            .icon {
              width: 24px;
              height: 24px;
            }

            .title {
              margin: 0 0 0 6px;
              font-weight: 600;
              font-size: 14px;
              color: #333333;
              line-height: 20px;
            }
          }

          .message-body {
            margin-top: 8px;
            display: flex;

            .cover {
              width: 74px;
              height: 74px;
              border-radius: 8px;
              flex-shrink: 0;
              object-fit: cover;
            }

            .right {
              display: flex;
              flex-direction: column;
              justify-content: space-between;
              flex: 1;
              min-width: 0;
              padding-left: 10px;

              .m-content {
                margin: 4px 0 0;
                font-weight: 400;
                font-size: 12px;
                color: #999999;
                line-height: 17px;
                @include text-overflow(1);
              }

              .right-bottom {
                margin-top: 2px;

                .m-type-label {
                  font-weight: 600;
                  font-size: 12px;
                  color: #333333;
                  line-height: 17px;
                }

                .m-sub-content {
                  margin-top: 3px;
                  font-weight: 400;
                  font-size: 12px;
                  color: #333333;
                  line-height: 17px;
                }
              }
            }
          }

          .message-footer {
            margin-top: 10px;
            display: flex;
            justify-content: flex-end;

            .btn-view-more {
              --van-button-round-radius: 10px;
              --van-button-default-height: 20px;
              --van-button-normal-font-size: 12px;

              &:active::before {
                background: transparent;
                border: none;
              }
            }
          }
        }
      }
    }
  }
}

.loading-area {
  min-height: 50vh;
  padding-top: 40px;
  text-align: center;
}

.empty {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: white;

  text-align: center;

  img {
    width: 118px;
    height: 118px;
  }

  .empty-title {
    font-size: 16px;
    color: #333333;
    margin-bottom: 10px;
  }

  .empty-sub-title {
    font-size: 12px;
    color: #666666;
  }
}

.logo {
  width: 27px;
  height: 27px;
  margin-right: 4px;
}

:deep(.van-notify) {
  width: 308px;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 15px;
  border-radius: 10px;
  background: black;
  color: white;
  opacity: 0.7;
}
</style>
